import { useEffect, useRef, useState } from "react";
import {
  userData,
  areObjectsEqual,
  invoiceStructure,
  inputOnEnter,
  formatValueUnit,
  focusElement,
} from "../js/utils";
import { post } from "../js/post";
import InputData from "./InputData";
import Dropdown from "./Dropdown";
import ButtonIconH from "./ButtonIconH";
import DataTable from "react-data-table-component";
import CheckBox from "./CheckBox";
import InvoiceDeliveryNotesSearcher from "./InvoiceDeliveryNotesSearcher";
import { useSuppliers } from "../hooks/useSuppliers";
import moment from "moment";
import {
  invoiceDeliveryNotesColumns,
  invoiceDueDatesColumns,
} from "../js/tableColumns";

export function Invoice({
  id,
  onClose,
  createPopupQuestion,
  removePopupQuestion,
}) {
  const [deliveryNotesHandler, setDeliveryNotesHandler] = useState(false);
  const [supplierList] = useSuppliers(-1, "");
  const [currentInvoice, setCurrentInvoice] = useState(invoiceStructure);
  const [currentInvoiceFixed, setCurrentInvoiceFixed] =
    useState(invoiceStructure);
  const isInitialLoad = useRef(false);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [paymentMethodSelected, setPaymentMethodSelected] = useState({
    id: "",
    text: "",
  });
  const [selectedSupplier, setSelectedSupplier] = useState({
    id: "",
    text: "",
    nif: "",
  });
  const [dueDatesGenerator, setDueDatesGenerator] = useState({
    num: 1,
    firstDate: moment().format("YYYY-MM-DD"),
    interval: 30,
  });

  // console.log("currentInvoice: ", currentInvoice);
  // console.log("currentInvoiceFixed: ", currentInvoiceFixed);
  // console.log("dueDatesGenerator: ", dueDatesGenerator);

  const invoiceDueDatesColumnsEdited = invoiceDueDatesColumns.map((col) => {
    if (col.name === "Pagado") {
      return {
        ...col,
        cell: (row, rowIndex) => (
          <CheckBox
            active={row.pagado}
            onClick={() => {
              setCurrentInvoice((prev) => ({
                ...prev,
                vencimientos: prev.vencimientos.map((v, i) => {
                  const isMatch =
                    row.idVencimiento !== -1
                      ? v.idVencimiento === row.idVencimiento
                      : i === rowIndex;
                  return isMatch ? { ...v, pagado: !v.pagado } : v;
                }),
              }));
            }}
          />
        ),
      };
    }

    if (col.name === "Importe") {
      return {
        ...col,
        cell: (row, rowIndex) =>
          row.pagado ? (
            formatValueUnit(row.importe, "€")
          ) : (
            <>
              <InputData
                tabIndex={-1}
                type="type-float2"
                size={1}
                input={row.importe}
                onChange={(e) => {
                  setCurrentInvoice((prev) => ({
                    ...prev,
                    vencimientos: prev.vencimientos.map((v, i) => {
                      const isMatch =
                        row.idVencimiento !== -1
                          ? v.idVencimiento === row.idVencimiento
                          : i === rowIndex;
                      return isMatch
                        ? { ...v, importe: parseFloat(e) || 0 }
                        : v;
                    }),
                  }));
                }}
              ></InputData>
            </>
          ),
      };
    }

    if (col.name === "") {
      return {
        ...col,
        cell: (row, rowIndex) => (
          <button
            onClick={() => {
              createPopupQuestion(
                "¿Desea eliminar el vencimiento?",
                async () => {
                  removeDueDate(row.idVencimiento, rowIndex);
                  removePopupQuestion();
                },
                () => {
                  removePopupQuestion();
                },
                "Sí",
                "No",
              );
            }}
          >
            <img
              src={`/images/remove-blue.svg`}
              className="icon-pref"
              alt="Eliminar"
            ></img>
          </button>
        ),
      };
    }

    return col;
  });

  const invoiceDeliveryNotesColumnsEdited = invoiceDeliveryNotesColumns.map(
    (col) => {
      if (col.name === "") {
        return {
          ...col,
          cell: (row) => (
            <button
              onClick={() => {
                createPopupQuestion(
                  "¿Desea eliminar el albarán?",
                  async () => {
                    removeDeliveryNote(row.idAlbaran);
                    removePopupQuestion();
                  },
                  () => {
                    removePopupQuestion();
                  },
                  "Sí",
                  "No",
                );
              }}
            >
              <img
                src={`/images/remove-blue.svg`}
                className="icon-pref"
                alt="Eliminar"
              ></img>
            </button>
          ),
        };
      }
      return col;
    },
  );

  const validateInvoice = () => {
    const validations = [
      {
        condition: currentInvoice.idProveedor === "",
        message: "Por favor, asigne un proveedor.",
        tabIndex: 100,
      },
      {
        condition: currentInvoice.nFactura === "",
        message: "Por favor, asigne un número de factura.",
        tabIndex: 101,
      },
      {
        condition: currentInvoice.fechaFactura === "",
        message: "Por favor, asigne una fecha de factura.",
        tabIndex: 102,
      },
      {
        condition: currentInvoice.albaranes.length === 0,
        message: "Es obligatorio añadir al menos un albarán.",
        tabIndex: null,
      },
      {
        condition: currentInvoice.vencimientos.length === 0,
        message: "Es obligatorio añadir al menos un vencimiento.",
        tabIndex: null,
      },
    ];

    const failed = validations.find((v) => v.condition);

    if (failed) {
      createPopupQuestion(
        failed.message,
        () => {
          if (failed.tabIndex) focusElement(failed.tabIndex);
          removePopupQuestion();
        },
        null,
        "Entendido",
      );
      return false;
    }

    return true;
  };

  const removeDeliveryNote = (id) => {
    setCurrentInvoice((prev) => ({
      ...prev,
      albaranes: prev.albaranes.filter((e) => e.idAlbaran !== id),
    }));
  };

  const removeDueDate = (idVencimiento, index) => {
    setCurrentInvoice((prev) => {
      const filtered =
        idVencimiento !== -1
          ? prev.vencimientos.filter((v) => v.idVencimiento !== idVencimiento)
          : prev.vencimientos.filter((_, i) => i !== index);
      const total = filtered.length;
      const renumbered = filtered.map((v, i) => ({
        ...v,
        nVencimiento: `${i + 1}/${total}`,
      }));
      return { ...prev, vencimientos: renumbered };
    });
  };

  const addDeliveryNoteToInvoice = (deliveryNotes) => {
    setCurrentInvoice((prev) => ({
      ...prev,
      albaranes: [
        ...prev.albaranes,
        ...deliveryNotes
          .filter((e) => !prev.albaranes.some((a) => a.idAlbaran === e.id))
          .map((e) => ({
            idAlbaran: e.id,
            nAlbaran: e.numeroAlbaran,
            base: e.pcnTotal,
            iva: e.ivaTotal,
            total: e.totalTotal,
          })),
      ],
    }));
  };

  async function getInvoice() {
    const response = await post(
      "support-extra/facturasAlbaranes/ObtenerFacturaAlbaran.php",
      {
        token: userData.token,
        idFacturaAlbaran: id,
      },
    );
    // console.log(
      // "LISTANDO FACTURA: ",
      // {
        // token: userData.token,
        // idFacturaAlbaran: id,
      // },
      // response,
    // );
    isInitialLoad.current = true;
    setCurrentInvoice(response);
    setCurrentInvoiceFixed(response);
  }

  async function saveInvoice() {
    const response = await post(
      "support-extra/facturasAlbaranes/GuardarFacturaAlbaran.php",
      {
        token: userData.token,
        ...currentInvoice,
        albaranes: currentInvoice.albaranes.map((e) => e.idAlbaran),
      },
    );
    // console.log(
      // "GUARDANDO FACTURA: ",
      // {
        // token: userData.token,
        // ...currentInvoice,
        // albaranes: currentInvoice.albaranes.map((e) => e.idAlbaran),
      // },
      // response,
    // );
    if (response.error) {
      createPopupQuestion(
        response.error,
        removePopupQuestion,
        null,
        "Entendido",
      );
    } else if (response.idFacturaAlbaran) {
      setCurrentInvoiceFixed(currentInvoice);
      createPopupQuestion(
        "Factura guardada correctamente.",
        removePopupQuestion,
        null,
        "Entendido",
      );
    }
  }

  const loadPaymentMethods = async () => {
    const response = await post("ListarFormasPago.php", {
      token: userData.token,
    });
    const paymentMethodsN = response.map((item) => {
      return {
        id: item.id,
        text: item.descripcion,
      };
    });
    setPaymentMethods([{ id: "", text: "" }, ...paymentMethodsN]);
  };

  const removeInvoice = async () => {
    await post("support-extra/facturasAlbaranes/EliminarFacturaAlbaran.php", {
      token: userData.token,
      idFacturaAlbaran: currentInvoice.idFacturaAlbaran,
      idUsuario: userData.id,
    });
    // console.log("ELIMINANDO FACTURA: ", {
    // token: userData.token,
    // idFacturaAlbaran: currentInvoice.idFacturaAlbaran,
    // idUsuario: userData.id,
    // });
  };

  const generateDueDates = () => {
    const { num, firstDate, interval } = dueDatesGenerator;

    if (!num || !firstDate || num < 1) return;

    const totalPendiente = currentInvoice.pendiente || 0;

    // Separar los existentes en pagados y no pagados
    const existingPaid = currentInvoice.vencimientos.filter((v) => v.pagado);
    const existingUnpaid = currentInvoice.vencimientos.filter((v) => !v.pagado);

    // Lo ya comprometido en los vencimientos no pagados existentes
    const alreadyAssigned = existingUnpaid.reduce(
      (sum, v) => sum + (v.importe || 0),
      0,
    );

    // Lo que queda para repartir entre los nuevos
    const remaining = parseFloat((totalPendiente - alreadyAssigned).toFixed(2));
    const amountPerNewDueDate =
      num > 0 ? parseFloat((remaining / num).toFixed(2)) : 0;

    const totalUnpaid = existingUnpaid.length + num;

    // Mantener los vencimientos existentes no pagados tal como están, solo renumerar
    const updatedExistingUnpaid = existingUnpaid.map((v, i) => ({
      ...v,
      nVencimiento: `${i + 1}/${totalUnpaid}`,
    }));

    // Generar los nuevos vencimientos con el importe restante repartido
    const newDueDates = [];
    for (let i = 0; i < num; i++) {
      const dueDate = moment(firstDate)
        .add(interval * i, "days")
        .format("YYYY-MM-DD");

      newDueDates.push({
        idVencimiento: -1,
        fechaVencimiento: dueDate,
        importe: amountPerNewDueDate,
        nVencimiento: `${existingUnpaid.length + i + 1}/${totalUnpaid}`,
        pagado: false,
      });
    }

    // Ajustar el último vencimiento nuevo para compensar el redondeo
    const allUnpaid = [...updatedExistingUnpaid, ...newDueDates];
    const sumImports = allUnpaid.reduce((sum, v) => sum + v.importe, 0);
    const difference = parseFloat((totalPendiente - sumImports).toFixed(2));

    if (allUnpaid.length > 0 && Math.abs(difference) > 0) {
      allUnpaid[allUnpaid.length - 1].importe = parseFloat(
        (allUnpaid[allUnpaid.length - 1].importe + difference).toFixed(2),
      );
    }

    setCurrentInvoice((prev) => ({
      ...prev,
      vencimientos: [...existingPaid, ...allUnpaid],
    }));
  };

  useEffect(() => {
    if (currentInvoice.formaPago !== "") {
      const foundMethod = paymentMethods.find(
        (e) => e.text === currentInvoice.formaPago,
      );

      setPaymentMethodSelected(foundMethod || { id: "", text: "" });
    }
  }, [currentInvoice.formaPago, paymentMethods]);

  useEffect(() => {
    if (currentInvoice.idProveedor !== "") {
      const foundSupplier = supplierList
        .map((e) => ({ id: e.id, text: e.nombre, nif: e.nif }))
        .find((e) => e.id === currentInvoice.idProveedor);

      setSelectedSupplier(foundSupplier || { id: "", text: "", nif: "" });
    }
  }, [currentInvoice.idProveedor, supplierList]);

  useEffect(() => {
    const base = currentInvoice.albaranes.reduce(
      (sum, a) => sum + (a.base || 0),
      0,
    );
    const iva = currentInvoice.albaranes.reduce(
      (sum, a) => sum + (a.iva || 0),
      0,
    );
    const total = currentInvoice.albaranes.reduce(
      (sum, a) => sum + (a.total || 0),
      0,
    );
    const abonado = currentInvoice.vencimientos
      .filter((v) => v.pagado)
      .reduce((sum, v) => sum + (v.importe || 0), 0);
    const pendiente = currentInvoice.vencimientos
      .filter((v) => !v.pagado)
      .reduce((sum, v) => sum + (v.importe || 0), 0);

    const recalculated = {
      base: parseFloat(base.toFixed(2)),
      iva: parseFloat(iva.toFixed(2)),
      total: parseFloat(total.toFixed(2)),
      abonado: parseFloat(abonado.toFixed(2)),
      pendiente: parseFloat(
        (parseFloat(total.toFixed(2)) - parseFloat(abonado.toFixed(2))).toFixed(
          2,
        ),
      ),
    };

    setCurrentInvoice((prev) => ({ ...prev, ...recalculated }));

    if (isInitialLoad.current) {
      setCurrentInvoiceFixed((prev) => ({ ...prev, ...recalculated }));
      isInitialLoad.current = false;
    }
  }, [currentInvoice.albaranes, currentInvoice.vencimientos]);

  useEffect(() => {
    loadPaymentMethods();
    if (id !== -1) getInvoice();
  }, [id]);

  return (
    <div className="popup-container">
      {deliveryNotesHandler && (
        <InvoiceDeliveryNotesSearcher
          supplier={selectedSupplier.text}
          onAdd={(deliveryNotes) => {
            addDeliveryNoteToInvoice(deliveryNotes);
            setDeliveryNotesHandler(false);
          }}
          onClose={() => {
            setDeliveryNotesHandler(false);
          }}
        />
      )}
      <div className="ap-container popup-screen">
        <div className="popup-screen-header">
          <span className="popup-screen-header-title">Ficha de factura</span>
          <button
            className="popup-action-icon close-icon"
            onClick={() => {
              if (!areObjectsEqual(currentInvoice, currentInvoiceFixed)) {
                // console.log(currentInvoice, currentInvoiceFixed);
                createPopupQuestion(
                  "Existen cambios sin guardar. ¿Desea salir de la ficha de factura?",
                  async () => {
                    removePopupQuestion();
                    onClose();
                  },
                  () => {
                    removePopupQuestion();
                  },
                  "Sí",
                  "No",
                );
              } else onClose();
            }}
          >
            <img src="/images/close-white.svg" loading="lazy" alt="Icon"></img>
          </button>
        </div>
        <div
          className={`tab-content ap-content`}
          style={{
            overflow: "unset",
            backgroundColor: "#f9f9f9",
            display: "flex",
            flexDirection: "column",
            gap: 10,
          }}
        >
          <div
            className="container-panel"
            style={{ display: "flex", flexDirection: "column", gap: 10 }}
          >
            <div className="opt-table-content grid-8" id="invoice-grid">
              <div className="cell cell-label cell-start-content">
                Proveedor (*)
              </div>
              <div className="cell">
                <Dropdown
                  tabIndex={100}
                  optionList={supplierList.map((e) => ({
                    id: e.id,
                    text: e.nombre,
                    nif: e.nif,
                  }))}
                  size={0}
                  op={selectedSupplier.text}
                  mandatory={false}
                  onChange={(e) => {
                    if (!supplierList.some((s) => s.id === e.id)) return;
                    setCurrentInvoice((prev) => ({
                      ...prev,
                      idProveedor: e.id,
                    }));
                  }}
                ></Dropdown>
              </div>
              <div className="cell"></div>
              <div className="cell cell-label cell-start-content">
                Nº factura (*)
              </div>
              <div className="cell">
                <InputData
                  tabIndex={101}
                  type="type-alphanum-ext"
                  size={0}
                  input={currentInvoice.nFactura}
                  onChange={(e) => {
                    setCurrentInvoice((prev) => ({
                      ...prev,
                      nFactura: e,
                    }));
                  }}
                ></InputData>
              </div>
              <div className="cell"></div>
              <div className="cell cell-label cell-start-content">
                Forma de pago
              </div>
              <div className="cell">
                <Dropdown
                  tabIndex={103}
                  optionList={paymentMethods}
                  size={0}
                  op={paymentMethodSelected.text}
                  mandatory={false}
                  onChange={(e) => {
                    setCurrentInvoice((prev) => ({
                      ...prev,
                      formaPago: e.text,
                    }));
                  }}
                />
              </div>
              <div className="cell cell-label cell-start-content">NIF</div>
              <div className="cell">
                <InputData
                  tabIndex={-1}
                  type="type-alphanum-ext"
                  size={0}
                  inactive={true}
                  input={selectedSupplier.nif}
                ></InputData>
              </div>
              <div className="cell"></div>
              <div className="cell cell-label cell-start-content">
                Fecha factura (*)
              </div>
              <div className="cell">
                <input
                  type="date"
                  max={new Date().toISOString().slice(0, 10)}
                  tabIndex={102}
                  className="input-field input-cell w-100"
                  value={currentInvoice.fechaFactura}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value && new Date(value) > new Date()) return;
                    setCurrentInvoice((prev) => ({
                      ...prev,
                      fechaFactura: value
                        ? moment(value).format("YYYY-MM-DD")
                        : "",
                    }));
                  }}
                  onKeyUp={(e) => inputOnEnter(e)}
                ></input>
              </div>
              <div className="cell"></div>
              <div className="cell cell-label cell-start-content">
                Nº tarjeta/IBAN
              </div>
              <div className="cell">
                <InputData
                  tabIndex={104}
                  type="type-alphanum-ext"
                  size={0}
                  input={currentInvoice.nTarjetaIBAN}
                  onChange={(e) => {
                    setCurrentInvoice((prev) => ({
                      ...prev,
                      nTarjetaIBAN: e,
                    }));
                  }}
                ></InputData>
              </div>
            </div>
          </div>
          <div style={{ display: "flex", gap: 10 }}>
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              <div
                className="container-panel invoice-tables"
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 10,
                  flex: 1,
                }}
              >
                <div className="opt-table-title opt-table-title-button">
                  <div>
                    Listado de albaranes (
                    {formatValueUnit(currentInvoice.albaranes.length)})
                  </div>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 30,
                    }}
                  >
                    <ButtonIconH
                      text="Añadir albaranes"
                      small={true}
                      highlight={true}
                      onClick={() => {
                        setDeliveryNotesHandler(true);
                      }}
                    ></ButtonIconH>
                  </div>
                </div>
                <DataTable
                  columns={invoiceDeliveryNotesColumnsEdited}
                  data={currentInvoice.albaranes}
                  className="custom-datatable"
                  noDataComponent="No hay albaranes disponibles."
                  onRowClicked={(row, event) => {}}
                />
              </div>
              <div
                className="container-panel invoice-tables"
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 10,
                  flex: 1,
                }}
              >
                <div
                  className="opt-table-content grid-10"
                  id="duedates-generator-grid"
                >
                  <div className="cell cell-label cell-start-content">
                    Nº vencimientos
                  </div>
                  <div className="cell">
                    <button
                      className="incdec-btn"
                      onClick={() => {
                        setDueDatesGenerator((prev) => ({
                          ...prev,
                          num:
                            parseInt(prev.num) > 1
                              ? parseInt(prev.num) - 1
                              : parseInt(prev.num),
                        }));
                      }}
                    >
                      -
                    </button>
                    <InputData
                      tabIndex={105}
                      type="type-positive-nonzero"
                      size={4}
                      input={dueDatesGenerator.num}
                      onChange={(e) => {
                        // console.log("PONIENDO: ", e);
                        setDueDatesGenerator((prev) => ({
                          ...prev,
                          num: e === "" ? 1 : e,
                        }));
                      }}
                    ></InputData>
                    <button
                      className="incdec-btn"
                      onClick={() => {
                        setDueDatesGenerator((prev) => ({
                          ...prev,
                          num: parseInt(prev.num) + 1,
                        }));
                      }}
                    >
                      +
                    </button>
                  </div>
                  <div className="cell"></div>
                  <div className="cell cell-label cell-start-content">
                    Primer vencimiento
                  </div>
                  <div className="cell">
                    <input
                      type="date"
                      max="9999-12-31"
                      tabIndex={106}
                      className="input-field input-cell w-100"
                      value={dueDatesGenerator.firstDate}
                      onChange={(e) => {
                        setDueDatesGenerator((prev) => ({
                          ...prev,
                          firstDate: moment(e.target.value).format(
                            "YYYY-MM-DD",
                          ),
                        }));
                      }}
                      onKeyUp={(e) => inputOnEnter(e)}
                    ></input>
                  </div>
                  <div className="cell"></div>
                  <div className="cell cell-label cell-start-content">
                    Días entre vencimientos
                  </div>
                  <div className="cell">
                    <InputData
                      tabIndex={107}
                      type="type-only-positive-numbers"
                      size={0}
                      input={dueDatesGenerator.interval}
                      onChange={(e) => {
                        setDueDatesGenerator((prev) => ({
                          ...prev,
                          interval: e,
                        }));
                      }}
                    ></InputData>
                  </div>
                  <div className="cell"></div>
                  <div className="cell">
                    <ButtonIconH
                      text="Generar vencimientos"
                      small={true}
                      disabled={currentInvoice.albaranes.length === 0}
                      highlight={true}
                      onClick={generateDueDates}
                    ></ButtonIconH>
                  </div>
                </div>
                <div
                  className="opt-table-title opt-table-title-button"
                  style={{ marginTop: 20 }}
                >
                  <div>
                    Listado de vencimientos (
                    {formatValueUnit(currentInvoice.vencimientos.length)})
                  </div>
                </div>
                <DataTable
                  columns={invoiceDueDatesColumnsEdited}
                  data={currentInvoice.vencimientos}
                  className="custom-datatable"
                  noDataComponent="No hay vencimientos disponibles."
                />
                <DataTable
                  columns={[
                    { name: "", selector: () => "", width: "25%" },
                    { name: "", selector: () => "", width: "30%" },
                    {
                      name: "",
                      selector: (row) => formatValueUnit(row.total, "€"),
                      width: "30%",
                      right: false,
                    },
                    { name: "", selector: () => "", width: "10%" },
                    { name: "", selector: () => "", width: "5%" },
                  ]}
                  data={[
                    {
                      total: parseFloat(
                        currentInvoice.vencimientos
                          .reduce((sum, v) => sum + (v.importe || 0), 0)
                          .toFixed(2),
                      ),
                    },
                  ]}
                  className="custom-datatable summary-row no-header-datatable"
                  noHeader
                ></DataTable>
              </div>
            </div>
            <div
              className="container-panel"
              style={{ display: "flex", flexDirection: "column", gap: 10 }}
            >
              <div className="opt-table-title">Desglose</div>
              <div
                className="opt-table-content grid-2"
                id="invoice-detail-grid"
              >
                <div className="cell cell-label cell-start-content">Base</div>
                <div className="cell">
                  <InputData
                    tabIndex={-1}
                    type="type-alphanum-ext"
                    size={0}
                    inactive={true}
                    input={formatValueUnit(currentInvoice.base, "€")}
                  ></InputData>
                </div>
                <div className="cell cell-label cell-start-content">IVA</div>
                <div className="cell">
                  <InputData
                    tabIndex={-1}
                    type="type-alphanum-ext"
                    size={0}
                    inactive={true}
                    input={formatValueUnit(currentInvoice.iva, "€")}
                  ></InputData>
                </div>
                <div className="cell cell-label cell-start-content">Total</div>
                <div className="cell">
                  <InputData
                    tabIndex={-1}
                    type="type-alphanum-ext"
                    size={0}
                    inactive={true}
                    input={formatValueUnit(currentInvoice.total, "€")}
                  ></InputData>
                </div>
                <div className="cell cell-label cell-start-content">
                  Abonado
                </div>
                <div className="cell">
                  <InputData
                    tabIndex={-1}
                    type="type-alphanum-ext"
                    size={0}
                    inactive={true}
                    input={formatValueUnit(currentInvoice.abonado, "€")}
                  ></InputData>
                </div>
                <div className="cell cell-label cell-start-content">
                  Pendiente
                </div>
                <div className="cell">
                  <InputData
                    tabIndex={-1}
                    type="type-alphanum-ext"
                    size={0}
                    inactive={true}
                    input={formatValueUnit(currentInvoice.pendiente, "€")}
                  ></InputData>
                </div>
                <div className="cell cell-label cell-start-content">
                  Factura pagada
                </div>
                <div className="cell cell-start-content">
                  <CheckBox
                    tabIndex={-1}
                    active={currentInvoice.pagada}
                    onClick={() => {
                      setCurrentInvoice((prev) => ({
                        ...prev,
                        pagada: !prev.pagada,
                      }));
                    }}
                  ></CheckBox>
                </div>
              </div>
            </div>
          </div>
          <div
            className="container-panel popup-buttons"
            style={{ justifyContent: "center" }}
          >
            <ButtonIconH
              text="Guardar"
              onClick={async () => {
                if (validateInvoice()) {
                  await saveInvoice();
                }
              }}
            ></ButtonIconH>
            <ButtonIconH
              text="Eliminar"
              onClick={async () => {
                createPopupQuestion(
                  "¿Desea eliminar la factura?",
                  async () => {
                    await removeInvoice();
                    removePopupQuestion();
                    onClose();
                  },
                  () => {
                    removePopupQuestion();
                  },
                  "Sí",
                  "No",
                );
              }}
              disabled={id === -1}
            ></ButtonIconH>
          </div>
        </div>
      </div>
    </div>
  );
}
